/**
 * AttachVaultDocumentsDialog Component
 *
 * Dialog shown after job creation to prompt user to attach Evidence Vault documents.
 * Pre-checks licenses and insurance if available.
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Archive,
  BadgeCheck,
  Shield,
  FileCheck,
  ArrowRight,
  Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getReusableEvidenceDocuments,
  linkVaultDocumentToJob,
} from "@/services/evidenceVaultService";
import type { EvidenceDocument, EvidenceDocumentType } from "@/services/mock/evidenceVaultData";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";

interface AttachVaultDocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobAddress: string;
  contractorId: string;
  onComplete: () => void;
  onSkip: () => void;
}

const TYPE_CONFIG: Record<string, { label: string; icon: typeof FileCheck; priority: number }> = {
  license: { label: "Licenses", icon: BadgeCheck, priority: 1 },
  insurance: { label: "Insurance", icon: Shield, priority: 2 },
  authorization: { label: "Authorizations", icon: FileCheck, priority: 3 },
  product_approval: { label: "Product Approvals", icon: FileCheck, priority: 4 },
};

export function AttachVaultDocumentsDialog({
  open,
  onOpenChange,
  jobId,
  jobAddress,
  contractorId,
  onComplete,
  onSkip,
}: AttachVaultDocumentsDialogProps) {
  const [loading, setLoading] = useState(true);
  const [attaching, setAttaching] = useState(false);
  const [documents, setDocuments] = useState<EvidenceDocument[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      loadDocuments();
    }
  }, [open, contractorId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const docs = await getReusableEvidenceDocuments(contractorId);
      setDocuments(docs);

      // Pre-select licenses and insurance (commonly needed)
      const preSelected = new Set<string>();
      docs.forEach(doc => {
        if (doc.type === 'license' || doc.type === 'insurance') {
          preSelected.add(doc.id);
        }
      });
      setSelectedIds(preSelected);
    } catch (error) {
      console.error("Failed to load vault documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDocument = (docId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedIds(newSelected);
  };

  const handleAttach = async () => {
    if (selectedIds.size === 0) {
      onComplete();
      return;
    }

    setAttaching(true);
    try {
      const selectedDocs = documents.filter(d => selectedIds.has(d.id));

      for (const doc of selectedDocs) {
        await linkVaultDocumentToJob(contractorId, doc, jobId, jobAddress);
      }

      toast({
        title: "Documents Attached",
        description: `${selectedDocs.length} document${selectedDocs.length > 1 ? 's' : ''} linked to your new job`,
      });

      onComplete();
    } catch (error) {
      console.error("Failed to attach documents:", error);
      toast({
        title: "Error",
        description: "Some documents could not be attached",
        variant: "destructive",
      });
    } finally {
      setAttaching(false);
    }
  };

  const isExpiringSoon = (dateString?: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return date <= thirtyDaysFromNow && date > new Date();
  };

  // Group documents by type and sort by priority
  const groupedDocs = documents.reduce((acc, doc) => {
    if (!acc[doc.type]) {
      acc[doc.type] = [];
    }
    acc[doc.type].push(doc);
    return acc;
  }, {} as Record<EvidenceDocumentType, EvidenceDocument[]>);

  const sortedGroups = Object.entries(groupedDocs).sort(([a], [b]) => {
    const priorityA = TYPE_CONFIG[a]?.priority ?? 99;
    const priorityB = TYPE_CONFIG[b]?.priority ?? 99;
    return priorityA - priorityB;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Attach Documents from Evidence Vault
          </DialogTitle>
          <DialogDescription>
            These documents will be automatically linked to your new job.
            Licenses and insurance are pre-selected.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8">
              <Archive className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="font-medium text-muted-foreground">No documents in Evidence Vault</p>
              <p className="text-sm text-muted-foreground mt-1 mb-4">
                Upload your license and insurance to attach them automatically to every job.
              </p>
              <Button asChild variant="outline" size="sm">
                <Link to="/evidence-vault">
                  Go to Evidence Vault
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          ) : (
            sortedGroups.map(([type, docs]) => {
              const config = TYPE_CONFIG[type] || { label: type, icon: FileCheck };
              const Icon = config.icon;

              return (
                <div key={type} className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Icon className="h-4 w-4" />
                    {config.label}
                  </div>
                  <div className="space-y-2">
                    {docs.map(doc => (
                      <label
                        key={doc.id}
                        className={cn(
                          "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                          selectedIds.has(doc.id)
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/50"
                        )}
                      >
                        <Checkbox
                          checked={selectedIds.has(doc.id)}
                          onCheckedChange={() => toggleDocument(doc.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">
                              {doc.name}
                            </span>
                            {isExpiringSoon(doc.expirationDate) && (
                              <Badge variant="outline" className="text-amber-600 border-amber-300 shrink-0">
                                <Clock className="h-3 w-3 mr-1" />
                                Expiring
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {doc.fileName}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="ghost" onClick={onSkip} disabled={attaching}>
            Skip
          </Button>
          <Button onClick={handleAttach} disabled={attaching}>
            {attaching ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Attaching...
              </>
            ) : selectedIds.size > 0 ? (
              `Attach ${selectedIds.size} Document${selectedIds.size > 1 ? 's' : ''}`
            ) : (
              "Continue without attaching"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AttachVaultDocumentsDialog;
